import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../store/store';

interface StatCardProps {
  label: string;
  value: string | number;
  icon: React.ReactNode;
}

const StatCard: React.FC<StatCardProps> = ({ label, value, icon }) => {
  return (
    <div
      style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '20px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        flex: '1',
        minWidth: '200px'
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#666' }}>
        {icon}
        <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>{label}</span>
      </div>
      <div style={{ fontSize: '2rem', fontWeight: 700, color: '#333' }}>{value}</div>
    </div>
  );
};

export const InsightsOverview: React.FC = () => {
  const orders = useSelector((state: RootState) => state.orders.orders);
  const completedOrders = useSelector((state: RootState) => state.orders.completedOrders);
  const clients = useSelector((state: RootState) => state.clients.clients);

  // Track the selected month offset (0 = current month, -1 = previous month, etc.)
  const [monthOffset, setMonthOffset] = useState(-1);

  // Calculate selected month date range
  const { selectedMonthName, selectedMonthStart, selectedMonthEnd, isCurrentMonth } = useMemo(() => {
    const today = new Date();
    const year =
      today.getMonth() + monthOffset < 0
        ? today.getFullYear() + Math.floor((today.getMonth() + monthOffset) / 12)
        : today.getFullYear();
    const month = (((today.getMonth() + monthOffset) % 12) + 12) % 12;

    const monthStart = new Date(year, month, 1);
    const monthEnd = new Date(year, month + 1, 0, 23, 59, 59, 999);

    const monthNames = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December'
    ];

    // Check if this is the current month
    const isCurrentMonth = month === today.getMonth() && year === today.getFullYear();

    return {
      selectedMonthName: `${monthNames[month]} ${year}`,
      selectedMonthStart: monthStart,
      selectedMonthEnd: monthEnd,
      isCurrentMonth
    };
  }, [monthOffset]);

  // Navigation handlers
  const handlePreviousMonth = () => {
    setMonthOffset(prev => prev - 1);
  };

  const handleNextMonth = () => {
    if (monthOffset < 0) {
      setMonthOffset(prev => prev + 1);
    }
  };

  // Calculate stats for selected month
  const stats = useMemo(() => {
    // New Orders: Orders created in selected month
    const newOrders = orders.filter(order => {
      const orderDate = new Date(order.orderDate);
      return orderDate >= selectedMonthStart && orderDate <= selectedMonthEnd;
    });

    // Completed Orders: Orders completed in selected month
    const completedInMonth = completedOrders.filter(order => {
      if (!order.completionDate) return false;
      const completionDate = new Date(order.completionDate);
      return completionDate >= selectedMonthStart && completionDate <= selectedMonthEnd;
    });

    // New Clients: Clients created in selected month
    const newClients = clients.filter(client => {
      if (!client.createdAt) return false;
      const createdAt = new Date(client.createdAt);
      return createdAt >= selectedMonthStart && createdAt <= selectedMonthEnd;
    });

    // Revenue: Total from completed orders in selected month
    const revenue = completedInMonth.reduce((total, order) => {
      return total + (order.pricing?.total || 0);
    }, 0);

    return {
      newOrders: newOrders.length,
      completedOrders: completedInMonth.length,
      newClients: newClients.length,
      revenue
    };
  }, [orders, completedOrders, clients, selectedMonthStart, selectedMonthEnd]);

  // Format currency
  const formatCurrency = (value: number): string => {
    return `$${value.toFixed(2)}`;
  };

  return (
    <div style={{ marginBottom: '32px' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '16px'
        }}
      >
        <button
          onClick={handlePreviousMonth}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#666',
            transition: 'color 0.2s'
          }}
          onMouseEnter={e => (e.currentTarget.style.color = '#333')}
          onMouseLeave={e => (e.currentTarget.style.color = '#666')}
          aria-label="Previous month"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>

        <h2
          style={{
            fontSize: '1.25rem',
            color: '#333',
            margin: 0,
            fontWeight: 600
          }}
        >
          Overview - {selectedMonthName}
        </h2>

        <button
          onClick={handleNextMonth}
          disabled={isCurrentMonth}
          style={{
            background: 'none',
            border: 'none',
            cursor: isCurrentMonth ? 'default' : 'pointer',
            padding: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: isCurrentMonth ? '#ddd' : '#666',
            transition: 'color 0.2s',
            opacity: isCurrentMonth ? 0.5 : 1
          }}
          onMouseEnter={e => {
            if (!isCurrentMonth) e.currentTarget.style.color = '#333';
          }}
          onMouseLeave={e => {
            if (!isCurrentMonth) e.currentTarget.style.color = '#666';
          }}
          aria-label="Next month"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '16px'
        }}
      >
        <StatCard
          label="New Orders"
          value={stats.newOrders}
          icon={
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
              <line x1="3" y1="6" x2="21" y2="6" />
              <path d="M16 10a4 4 0 0 1-8 0" />
            </svg>
          }
        />

        <StatCard
          label="Completed Orders"
          value={stats.completedOrders}
          icon={
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
          }
        />

        <StatCard
          label="New Clients"
          value={stats.newClients}
          icon={
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
          }
        />

        <StatCard
          label="Revenue"
          value={formatCurrency(stats.revenue)}
          icon={
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="12" y1="1" x2="12" y2="23" />
              <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
            </svg>
          }
        />
      </div>
    </div>
  );
};
